import { api } from '../../lib/api.ts';
import type { Role } from '../../store/auth.ts';

export type MeetingType = 'GENERAL' | 'EMERGENCY';
export type Body = 'COUNCIL' | 'WDC';
export type AgendaCategory = 'GOVT_OFFICES' | 'COUNCIL_MEMBERS' | 'COUNCIL_ADMIN' | 'OTHER';

// Fixed agenda section order — mirrors the backend SECTION_ORDER. The meeting is
// run in this order, so the yaumiyya editor lists «މައްސަލަ» the same way (group
// by section, then orderIndex) to match the agenda PDF.
export const AGENDA_SECTION_ORDER: AgendaCategory[] = ['GOVT_OFFICES', 'COUNCIL_MEMBERS', 'COUNCIL_ADMIN', 'OTHER'];

// Sort agenda items into the order the agenda PDF numbers them: section first,
// then orderIndex within the section. Returns a new array.
export function sortByAgendaOrder<T extends { category: AgendaCategory; orderIndex: number | null }>(items: T[]): T[] {
  const rank = (c: AgendaCategory): number => {
    const i = AGENDA_SECTION_ORDER.indexOf(c);
    return i === -1 ? AGENDA_SECTION_ORDER.length : i;
  };
  return [...items].sort((a, b) =>
    (rank(a.category) - rank(b.category)) || ((a.orderIndex ?? 0) - (b.orderIndex ?? 0)));
}

export interface MeetingRow {
  id: string; orgId: string; body: Body; year: number; sequence: number; title: string;
  scheduledAt: string; location: string; meetingType: MeetingType;
  termId: string | null;
  status: 'SCHEDULED' | 'AGENDA_FINALIZED' | 'CANCELLED';
  // Signing + minutes state — used to derive the single overall meeting status.
  agendaSignDocId: string | null;
  agendaPassedAt: string | null;
  // The userIds the signed agenda is emailed to (chosen at creation, editable
  // until the agenda passes).
  agendaRecipientIds: string[];
  // Set when the meeting is marked completed (read-only); null when reopened.
  completedAt: string | null;
  completedById: string | null;
  yaumiyya: { status: 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' } | null;
  createdBy: { id: string; name: string; nameDv?: string | null };
  _count: { agendaItems: number };
}

export interface AttachmentRow { id: string; fileName: string; mimeType: string; size: number }

export interface AgendaItemRow {
  id: string; title: string; description: string | null; category: AgendaCategory;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  rejectReason: string | null; orderIndex: number | null;
  createdAt: string; decidedAt: string | null;
  // enteredById = who actually submitted it (SG/president for on-behalf items);
  // enteredByName = their display name, set only when submitted on behalf.
  enteredById?: string | null; enteredByName?: string | null;
  submittedBy: { id: string; name: string; nameDv?: string | null; role: Role; designationDv?: string | null };
  attachments: AttachmentRow[];
}

export interface AgendaDocRow { id: string; version: number; finalized: boolean; generatedAt: string; rescheduled: boolean }

export interface TransferOutRow {
  id: string; toMeetingId: string; count: number; itemTitles: string[]; transferredAt: string;
  toMeeting: { id: string; title: string; year: number; sequence: number } | null;
}

// Per-document number state for the Numbers panel. `format` is the template
// (e.g. "AGD/438/{TERM}/{YYYY}/{###}"); `value` is the effective sequence integer
// (claimed, reserved, or the next-in-order default); `locked` once the document is
// committed to signing.
export interface NumberInfo { format: string | null; value: number | null; locked: boolean }
export interface MeetingNumbering {
  termNumber: number | null;
  year: number;
  agenda: NumberInfo;
  yaumiyya: NumberInfo;
  ninmun: NumberInfo;
}

export interface MeetingDetail extends MeetingRow {
  agendaItems: AgendaItemRow[];
  documents: AgendaDocRow[];
  // The user currently chairing this meeting's body (succession-aware: president
  // → VP → members, skipping anyone on leave). All president-gated UI compares
  // this to the viewer's id. Null when everyone in the chain is on leave.
  actingChairId: string | null;
  actingChairName: string | null;
  // Numbering state for the three official documents (agenda/yaumiyya/ninmun).
  numbering: MeetingNumbering;
  // Notes for items transferred OUT of this meeting to other scheduled meetings.
  transfersOut?: TransferOutRow[];
  // The agenda-send receipt: the recorded outcome of emailing the signed agenda
  // (from the 'agenda.emailed' audit entry). Null for meetings whose agenda was
  // sent before this record existed — the UI falls back to agendaPassedAt.
  agendaSend: {
    sentAt: string;
    method: string;
    recipientCount: number | null;
    success: boolean;
    error: string | null;
  } | null;
  // Reschedule state: the meeting's original scheduledAt (set the first time it
  // is rescheduled, so the UI can show "was <original>"), the sign doc created
  // for re-signing the rescheduled agenda, and when that re-signed agenda passed.
  originalScheduledAt: string | null;
  rescheduledAgendaSignDocId: string | null;
  rescheduledAgendaPassedAt: string | null;
}

export const meetingsApi = {
  list: (filter?: 'upcoming' | 'past', page = 1, term?: string, search?: string, body?: Body, excludeCancelled?: boolean, pageSize?: number) =>
    api.get<{ meetings: MeetingRow[]; total: number }>('/meetings', { params: { filter, page, pageSize, term, search: search || undefined, body, excludeCancelled: excludeCancelled || undefined } }).then((r) => r.data),
  get: (id: string) => api.get<{ meeting: MeetingDetail }>(`/meetings/${id}`).then((r) => r.data.meeting),
  create: (input: { title: string; scheduledAt: string; location: string; meetingType?: MeetingType; body: Body; recipientIds: string[] }) =>
    api.post<{ meeting: MeetingRow }>('/meetings', input).then((r) => r.data.meeting),
  update: (id: string, patch: { title?: string; scheduledAt?: string; location?: string; meetingType?: MeetingType; status?: 'CANCELLED' }) =>
    api.patch<{ meeting: MeetingRow }>(`/meetings/${id}`, patch).then((r) => r.data.meeting),
  // Mark the meeting completed (read-only). President or SG.
  complete: (id: string) =>
    api.post<{ meeting: MeetingRow }>(`/meetings/${id}/complete`, {}).then((r) => r.data.meeting),
  // Reopen a completed meeting. Elected President only.
  reopenCompleted: (id: string) =>
    api.post<{ meeting: MeetingRow }>(`/meetings/${id}/reopen-completed`, {}).then((r) => r.data.meeting),
  // Replace the agenda-email recipients (allowed until the agenda passes).
  updateRecipients: (id: string, recipientIds: string[]) =>
    api.patch<{ meeting: MeetingDetail }>(`/meetings/${id}/recipients`, { recipientIds }).then((r) => r.data.meeting),
  // Manually override one or more document sequence numbers (only the integer).
  updateNumbers: (id: string, patch: { agenda?: number; yaumiyya?: number; ninmun?: number }) =>
    api.patch<{ meeting: MeetingDetail }>(`/meetings/${id}/numbers`, patch).then((r) => r.data.meeting),
  // Hard-delete a meeting (president-only, body-scoped on the server).
  remove: (id: string) => api.delete(`/meetings/${id}`),
  submitItem: (meetingId: string, form: FormData) =>
    api.post<{ item: AgendaItemRow }>(`/meetings/${meetingId}/items`, form).then((r) => r.data.item),
  updateItem: (itemId: string, form: FormData) =>
    api.post<{ item: AgendaItemRow }>(`/items/${itemId}`, form).then((r) => r.data.item),
  deleteItem: (itemId: string) =>
    api.delete(`/items/${itemId}`),
  decide: (itemId: string, decision: 'APPROVED' | 'REJECTED', rejectReason?: string) =>
    api.post<{ item: AgendaItemRow }>(`/items/${itemId}/decision`, { decision, rejectReason }).then((r) => r.data.item),
  reorder: (meetingId: string, itemIds: string[]) =>
    api.post(`/meetings/${meetingId}/items/reorder`, { itemIds }),
  // Transfer (defer) APPROVED items to another SCHEDULED meeting. Returns count.
  transferItems: (meetingId: string, targetMeetingId: string, itemIds: string[]) =>
    api.post<{ moved: number }>(`/meetings/${meetingId}/transfer-items`, { targetMeetingId, itemIds }).then((r) => r.data.moved),
  // Upcoming SCHEDULED meetings (across the active term), excluding `excludeId`.
  // Used as transfer targets — a SCHEDULED meeting re-finalizes its agenda later.
  listScheduled: (excludeId?: string) =>
    api.get<{ meetings: MeetingRow[]; total: number }>('/meetings', { params: { filter: 'upcoming', page: 1 } })
      .then((r) => r.data.meetings.filter((m) => m.status === 'SCHEDULED' && m.id !== excludeId)),
  finalize: (meetingId: string) =>
    api.post<{ meeting: { id: string; status: string } }>(`/meetings/${meetingId}/finalize`).then((r) => r.data.meeting),
  reopen: (meetingId: string) =>
    api.post<{ meeting: { id: string; status: string } }>(`/meetings/${meetingId}/reopen`).then((r) => r.data.meeting),
  sendForSigning: (meetingId: string) =>
    api.post<{ signDocId: string; editorPath: string }>(`/meetings/${meetingId}/send-for-signing`).then((r) => r.data),
  regenerateDoc: (meetingId: string) =>
    api.post(`/meetings/${meetingId}/documents/regenerate`),
  // Reschedule a meeting to a new date/time (and optionally a new location).
  reschedule: (id: string, body: { scheduledAt: string; location?: string }) =>
    api.post<{ meeting: MeetingDetail }>(`/meetings/${id}/reschedule`, body).then((r) => r.data.meeting),
  // Send the rescheduled agenda for re-signing.
  rescheduleSendForSigning: (id: string) =>
    api.post<{ signDocId: string; editorPath: string }>(`/meetings/${id}/reschedule/send-for-signing`, {}).then((r) => r.data),
  attachmentUrl: (id: string) => `/api/attachments/${id}`,
  documentUrl: (id: string) => `/api/documents/${id}/file`,
  // Merged agenda download (original alone, or original+rescheduled in one PDF).
  agendaUrl: (id: string) => `/api/meetings/${id}/agenda.pdf`,
};

// SSO handoff into WapSign for the current user, redirecting to `redirect`
// (e.g. a document editor path). Returns the absolute WapSign URL.
export const wapsignApi = {
  handoff: (redirect: string) =>
    api.get<{ url: string }>('/integrations/wapsign/handoff', { params: { redirect } }).then((r) => r.data.url),
};

export async function downloadAuthed(url: string, fileName: string): Promise<void> {
  const r = await api.get(url.replace(/^\/api/, ''), { responseType: 'blob' });
  const blob = r.data as Blob;
  // A JSON error body can arrive as a blob on some failures — guard against
  // silently saving an error file as a PDF.
  if (blob.type && blob.type.includes('application/json')) {
    throw new Error('download_failed');
  }
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl; a.download = fileName; a.click();
  // Revoke after a short delay so Safari has time to initiate the download
  // before the object URL is torn down (immediate revoke breaks Safari).
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1_000);
}
